import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import BeachAccessIcon from "@mui/icons-material/BeachAccess";
import ChecklistIcon from "@mui/icons-material/Checklist";
import DashboardIcon from "@mui/icons-material/Dashboard";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import YoutubeSearchedForIcon from "@mui/icons-material/YoutubeSearchedFor";
import { Box, BoxProps, Chip, Icon, styled } from "@mui/material";
import { hasPermission, PolicyAction, PolicyResource } from "@vmosolutions/oc-interfaces";
import { useDispatch, useSelector } from "react-redux";
import { appendNodeToFirstValidLeaf, openWindow } from "src/redux/actions/Mosaic.Actions";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { ViewId } from "src/redux/reducers/MosaicManagerReducer";
import { isDevEnv } from "src/utils";
import { IMosaicTabNode } from "src/utils/MosaicTree";
import { v1 } from "uuid";
import { getCustomerFeature } from "src/features/featureRegistry";
import AdminIcon from "./icons/admin.svg?react";
import BidMgmtIcon from "./icons/bidmgmt.svg?react";
import BookingsIcon from "./icons/bookings.svg?react";
import BuilderIcon from "./icons/builder.svg?react";
import ContentMgmtIcon from "./icons/contentmgmt.svg?react";
import CrewIcon from "./icons/crew.svg?react";
import PairingBuilderIcon from "./icons/pairing_builder.svg?react";
import ReportsIcon from "./icons/reports.svg?react";
import ScheduleIcon from "./icons/schedule.svg?react";
import TimelineIcon from "./icons/timeline.svg?react";

const DocManagerIcon = (): JSX.Element => {
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <Icon
        component={FolderOpenIcon}
        style={{
          fontSize: 50,
        }}
      />
      <Icon
        component={AdminPanelSettingsIcon}
        style={{
          height: "25px",
          strokeWidth: "1px",
          stroke: "#878787",
          position: "absolute",
          bottom: 4,
          right: -12,
        }}
      />
    </div>
  );
};

export const LinkBox: React.FC<BoxProps> = styled((props: BoxProps) => <Box {...props} />)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  borderLeft: "3px solid #FA924B",
  padding: theme.spacing(0.75),
  marginTop: "2px",
  marginBottom: "2px",
  display: "flex",
  alignItems: "center",
  color: theme.palette.text.secondary,
  textTransform: "uppercase",
  fontSize: "1.1em",
  fontWeight: 900,
  transition: theme.transitions.create(["background-color", "border-left", "color"], {
    duration: theme.transitions.duration.standard,
  }),
  "& .chips": {
    marginLeft: "auto",
    "& .MuiChip-root": {
      borderRadius: theme.shape.borderRadius,
      fontSize: "0.8em",
      color: theme.palette.common.white,
      boxShadow: `0px 0px 3px ${theme.palette.divider}`,
      marginLeft: "2px",
    },
  },
  "&:not(.disabled)": {
    "&: hover": {
      backgroundColor: "#FA924B",
      borderLeft: `10px solid ${theme.palette.common.white}`,
      color: theme.palette.common.white,
      cursor: "pointer",
      transition: theme.transitions.create(["background-color", "border-left", "color"], {
        duration: "550ms",
      }),
    },
    "& svg": {
      flexShrink: 0,
      fill: theme.palette.text.primary,
      width: "26px",
      height: "26px",
      marginLeft: theme.spacing(0.2),
      marginRight: theme.spacing(2),
    },
  },
  "&.disabled": {
    backgroundColor: theme.palette.grey[900],
    borderLeft: `3px solid ${theme.palette.grey[700]}`,
    color: theme.palette.grey[700],
    "& svg": {
      width: "30px",
      height: "30px",
      marginRight: theme.spacing(2),
      ".st0": {
        fill: theme.palette.grey[700],
      },
    },
  },
}));

interface IAppLaunchPadLinkItem {
  title: string;
  mosiacPanel?: string;
  icon: JSX.Element;
  onClick?: () => void;
  isPermissed: boolean;
  isDisabled?: boolean;
  isDev?: boolean;
  tooltipText?: string;
  chip?: JSX.Element;
}

export const NavLinks = (): JSX.Element => {
  const biddingSettings = useSelector((state: IRootState) => state.crewSchedule.biddingSettings);
  const vacationBiddingSettings = useSelector((state: IRootState) => state.permissions.vacationBidding);
  const authentication = useSelector((state: IRootState) => state.authentication);
  const dispatch = useDispatch();
  const handleClickLink = (node: IMosaicTabNode): void => {
    {
      const { title, componentName, props, type } = node;
      if (type === "singleContext" || type === "context") {
        dispatch(openWindow(title, componentName as ViewId, [], props, type, true));
      } else {
        dispatch(appendNodeToFirstValidLeaf(node));
      }
    }
  };

  const NavLinkClick =
    (componentName: ViewId, title = "", props: any = {}, mode?: "context" | "content") =>
    () => {
      handleClickLink({
        componentName,
        id: v1(),
        title,
        props,
        type: mode ? mode : "content",
      });
    };

  const appLinkItems: IAppLaunchPadLinkItem[] = [
    {
      title: "Flight Display",
      mosiacPanel: "flightDisplay",
      icon: <TimelineIcon />,
      onClick: NavLinkClick("FlightTimeline", "Flight Display"),
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.FlightDisplay, PolicyAction.Read),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "Flight Status",
      mosiacPanel: "flightDisplay",
      icon: <ChecklistIcon />,
      onClick: NavLinkClick("FlightStatusWindow", "Flight Status", { tableDisplay: true }, "context"),
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.FlightStatus, PolicyAction.Read),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "Dispatch Desks",
      mosiacPanel: "DispatchDeskWindow",
      icon: <DashboardIcon />,
      onClick: NavLinkClick("DispatchDeskWindow", "Dispatch Desks"),
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.DispatchDesk, PolicyAction.Read),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "OpsLink",
      mosiacPanel: "OpsLinkWindow",
      icon: <BuilderIcon />,
      onClick: NavLinkClick("OpsLinkWindow", "OpsLink"),
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.Pairing, PolicyAction.BulkUpdate),
    },
    {
      title: "Crew Schedule",
      mosiacPanel: "CrewScheduleWindow",
      icon: <ScheduleIcon />,
      onClick: NavLinkClick("CrewScheduleWindow", "Crew Schedule"),
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.CrewSchedule, PolicyAction.Read),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "Flight Builder",
      mosiacPanel: "FlightBuilder",
      icon: <BuilderIcon />,
      onClick: NavLinkClick("FlightBuilder", "Flight Builder"),
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.Flight, PolicyAction.BulkCreate),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "Pairing Builder",
      mosiacPanel: "PairingBuilderWindow",
      icon: <PairingBuilderIcon />,
      onClick: NavLinkClick("PairingBuilderWindow", "Pairing Builder"),
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.Pairing, PolicyAction.BulkUpdate),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "Advanced Search",
      mosiacPanel: "AdvancedSearchWindow",
      icon: <YoutubeSearchedForIcon />,
      onClick: NavLinkClick("AdvancedSearchWindow", "Advanced Search", {}, "context"),
      isPermissed:
        hasPermission(authentication.attributes.policies, PolicyResource.Flight, PolicyAction.Read) ||
        hasPermission(authentication.attributes.policies, PolicyResource.GroundEvent, PolicyAction.Read) ||
        hasPermission(authentication.attributes.policies, PolicyResource.Pairing, PolicyAction.Read),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "Content Management",
      mosiacPanel: "ContentListWindow",
      icon: <ContentMgmtIcon />,
      onClick: NavLinkClick("ContentListWindow", "Content Management"),
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.Content, PolicyAction.List),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "Crew Travel",
      mosiacPanel: "CrewTravelWindow",
      icon: <BookingsIcon />,
      onClick: NavLinkClick("CrewTravelWindow", "Crew Travel"),
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.Hotel, PolicyAction.Use),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "Reporting",
      mosiacPanel: "ReportTreeViewWindow",
      icon: <ReportsIcon />,
      onClick: NavLinkClick("ReportTreeViewWindow", "Reporting"),
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.Report, PolicyAction.Read),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "View History",
      mosiacPanel: "HistoryWindow",
      icon: <HistoryOutlinedIcon />,
      onClick: NavLinkClick("HistoryWindow", "View History", {}, "context"),
      isPermissed:
        hasPermission(authentication.attributes.policies, PolicyResource.Flight, PolicyAction.Read) &&
        hasPermission(authentication.attributes.policies, PolicyResource.Pairing, PolicyAction.Read),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "Bid Package Management",
      mosiacPanel: "BidPackageListWindow",
      icon: <BidMgmtIcon />,
      onClick: NavLinkClick("BidPackageListWindow", "Bid Package Management"),
      isPermissed:
        hasPermission(authentication.attributes.policies, PolicyResource.Bidding, PolicyAction.Read) &&
        biddingSettings?.enabled,
      isDisabled: false,
      isDev: false,
    },
    {
      title: "Vacation Bid Management",
      mosiacPanel: "VacationBidPackageListWindow",
      icon: <BeachAccessIcon />,
      onClick: NavLinkClick("VacationBidPackageListWindow", "Vacation Bid Management"),
      isPermissed:
        hasPermission(authentication.attributes.policies, PolicyResource.VacationBidding, PolicyAction.Read) &&
        vacationBiddingSettings?.enabled,
      isDisabled: false,
      isDev: false,
    },
    {
      title: "Crew Web",
      icon: <CrewIcon />,
      onClick: () => {
        window.location.replace("/crew/");
      },
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.CrewWeb, PolicyAction.Use),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "EFB Document Manager",
      icon: <DocManagerIcon />,
      onClick: () => {
        window.location.replace("/docmanager/");
      },
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.EFBDocManager, PolicyAction.Use),
      isDisabled: false,
      isDev: false,
    },
    {
      title: "Administration",
      mosiacPanel: "AdminWindow",
      icon: <AdminIcon />,
      onClick: NavLinkClick("AdminWindow", "Admin"),
      isPermissed: hasPermission(authentication.attributes.policies, PolicyResource.SystemAdmin, PolicyAction.Use),
      isDisabled: false,
      isDev: false,
    },
  ];

  const customerFeature = getCustomerFeature();
  if (customerFeature) {
    appLinkItems.push({
      title: customerFeature.navLink.title,
      mosiacPanel: customerFeature.navLink.mosiacPanel,
      icon: customerFeature.navLink.icon,
      onClick: NavLinkClick(customerFeature.viewId as ViewId, customerFeature.navLink.title),
      isPermissed: true,
      isDisabled: false,
      isDev: false,
    });
  }

  const availableAppLinks = appLinkItems.filter((linkItem) => linkItem.isPermissed && (!linkItem.isDev || isDevEnv()));

  return (
    <>
      {authentication.attributes.policies.length > 0 &&
        availableAppLinks.map((linkItem: IAppLaunchPadLinkItem, key: number) => {
          return (
            <LinkBox
              key={key}
              className={linkItem.isDisabled ? "disabled" : ""}
              onClick={() => {
                if (linkItem.onClick) linkItem.onClick();
              }}
              sx={(theme) => ({
                backgroundColor: linkItem.isDev ? theme.palette.dev.dark : theme.palette.background.paper,
              })}
            >
              {linkItem.icon} {linkItem.title}
              <Box className="chips">
                {linkItem.chip}
                {linkItem.isDev && <Chip label="dev" color="primary" size="small" />}
              </Box>
            </LinkBox>
          );
        })}
    </>
  );
};
